import { Radio } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";

import { useSession } from "../../api/hooks.js";
import { signIn } from "../../api/session.js";
import { Button } from "../../design-system/button.js";
import { Field, TextInput } from "../../design-system/form.js";
import { Notice } from "../../design-system/surfaces.js";

/**
 * One screen, two fields.
 *
 * The product mounts sign-in and sign-out and nothing else, so there is no sign-up
 * link, no password reset and no account management here — offering any of them would
 * be an affordance for a route that does not exist. It stands outside the shell: a
 * page that is about getting in should not be framed by the thing you get into.
 */
export function SignInPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const session = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const next = safeNext(params.get("next"));

  // Already signed in — including the moment the sign-in above succeeds and the
  // session store tells every watcher. Redirecting is the render, never a side
  // effect of one.
  if (session) return <Navigate replace to={next} />;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    signIn({ email: email.trim(), password })
      .then(() => {
        void navigate(next, { replace: true });
      })
      .catch((cause: unknown) => {
        setError(
          cause instanceof Error ? cause.message : "Sign-in did not complete.",
        );
      })
      .finally(() => {
        setBusy(false);
      });
  }

  return (
    <main className="grid min-h-svh place-items-center px-4 py-10">
      <div className="w-full max-w-[24rem]">
        <div className="flex items-center gap-2.5">
          <span className="grid size-8 place-items-center rounded-md bg-accent text-accent-ink">
            <Radio aria-hidden="true" className="size-4" />
          </span>
          <span className="text-base font-semibold tracking-tight text-ink">
            Radio
          </span>
        </div>

        <h1 className="mt-8 text-2xl font-semibold tracking-tight text-ink">
          Sign in
        </h1>
        <p className="mt-1.5 text-sm leading-6 text-ink-3">
          Sign in to manage feeds. You don't need to sign in to listen.
        </p>

        <form className="mt-7 flex flex-col gap-4" noValidate onSubmit={submit}>
          <Field htmlFor="sign-in-email" label="Email">
            <TextInput
              autoComplete="username"
              autoFocus
              id="sign-in-email"
              name="email"
              onChange={(event) => {
                setEmail(event.target.value);
              }}
              placeholder="operator@example.com"
              required
              type="email"
              value={email}
            />
          </Field>

          <Field htmlFor="sign-in-password" label="Password">
            <TextInput
              autoComplete="current-password"
              id="sign-in-password"
              name="password"
              onChange={(event) => {
                setPassword(event.target.value);
              }}
              required
              type="password"
              value={password}
            />
          </Field>

          {error ? <Notice tone="danger">{error}</Notice> : null}

          <Button
            className="mt-1 w-full"
            disabled={busy || email === "" || password === ""}
            type="submit"
          >
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        <p className="mt-6 text-xs leading-5 text-ink-3">
          <Link
            className="text-accent-text underline-offset-4 hover:underline"
            to="/feeds"
          >
            Back to listening
          </Link>
        </p>
      </div>
    </main>
  );
}

/**
 * Where to go after signing in. Only a path inside this app is honoured — a `next`
 * pointing at another origin is an open redirect, and one that starts `//` is one in
 * disguise.
 */
export function safeNext(value: string | null): string {
  if (!value || !value.startsWith("/") || value.startsWith("//"))
    return "/feeds";
  return value;
}
